/**
 * Firebase Functions API Service
 * 
 * This service provides a centralized way to call Firebase Cloud Functions
 * for backend operations like YunExpress shipping rates.
 */

// Firebase Functions base URL
const FIREBASE_FUNCTIONS_BASE_URL = 
  process.env.VUE_APP_FIREBASE_FUNCTIONS_URL || 
  'https://us-central1-myoxytocin000-e3049.cloudfunctions.net';

/**
 * Call Firebase Function API endpoint
 * @param {string} endpoint - API endpoint (e.g., '/api/rates')
 * @param {Object} options - Fetch options
 * @returns {Promise<Object>} Response data
 */
async function callFirebaseFunction(endpoint, options = {}) {
  const url = `${FIREBASE_FUNCTIONS_BASE_URL}${endpoint}`;
  
  const defaultOptions = {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
  };

  const fetchOptions = {
    ...defaultOptions,
    ...options,
    headers: {
      ...defaultOptions.headers,
      ...(options.headers || {}),
    },
  };

  try {
    const response = await fetch(url, fetchOptions);
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `API request failed: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error(`[Firebase API] Error calling ${endpoint}:`, error);
    throw error;
  }
}

/**
 * Get YunExpress shipping rates
 * @param {Object} params - Shipping parameters
 * @param {string} params.country - Country code (e.g., 'US')
 * @param {string} params.zipCode - Postal/ZIP code
 * @param {number} params.weight - Package weight in kg
 * @param {number} [params.length] - Package length in cm
 * @param {number} [params.width] - Package width in cm
 * @param {number} [params.height] - Package height in cm
 * @param {string} [params.origin] - Optional YunExpress origin code (e.g., 'YT-HK')
 * @returns {Promise<Object>} Shipping rates (raw YunExpress trial JSON)
 */
export async function getShippingRates({ country, zipCode, weight = 0.5, length, width, height, origin }) {
  return callFirebaseFunction('/api/rates', {
    method: 'POST',
    body: JSON.stringify({
      country,
      zipCode,
      weight,
      length,
      width,
      height,
      origin,
    }),
  });
}

/**
 * Check Firebase Functions API health
 * @returns {Promise<Object>} Health status
 */
export async function checkApiHealth() {
  return callFirebaseFunction('/api/health');
}

export default {
  getShippingRates,
  checkApiHealth,
  callFirebaseFunction,
};
